import { useZeroSelector } from "../../hooks/use-zero-selector.js";
import { queries } from "@/lib/queries";
import { useCurrentUser } from "../current-user";
import { createIncrementalMapSelector } from "../incremental-map-selector";
import { RENDER_DEBUG } from "../debug-flags";

// ─── Row + output types ──────────────────────────────────────────────
// The composite query in packages/shared/src/queries/sidebar.ts returns
// channel_members rows with `.channel` (and its `.members[]` + `member.user`)
// and `.stats` eagerly loaded via `.related()`. We model that shape here as
// loosely typed `any` locally because the exact Zero-inferred type is deep
// and the selectors only read well-known fields.

/* eslint-disable @typescript-eslint/no-explicit-any */
type MembershipRow = any;
type ChannelRow = any;
type MembershipWithRelated = MembershipRow & {
  channel: ChannelRow | null;
  stats: ChannelStatsRow | null;
};
type ChannelStatsRow = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

export type SidebarTree = {
  /** Channel row (with pre-joined `.members[]` + `member.user`) keyed by channel.id. */
  channelMap: Map<string, ChannelRow>;
  /** Current user's channel_members row keyed by channel_id. Carries muted/hidden/notify. */
  membershipMap: Map<string, MembershipRow>;
  /** Channel IDs where the current user's membership has muted=true. */
  mutedSet: Set<string>;
  /** channel_id → unread_count (0 if no row or 0 unread). */
  unreadMap: Map<string, number>;
  /** channel_id → mention_count (0 if no row or 0 mentions). */
  mentionMap: Map<string, number>;
  /** channel_id → last_message_at (0 if no row or null). */
  lastMessageMap: Map<string, number>;
};

// ─── Shared empty constants (identity-stable defaults) ───────────────
// Frozen so accidental writes fail loudly in dev.
const EMPTY_CHANNEL_MAP = Object.freeze(new Map()) as Map<string, ChannelRow>;
const EMPTY_MEMBERSHIP_MAP = Object.freeze(new Map()) as Map<
  string,
  MembershipRow
>;
const EMPTY_MUTED_SET = Object.freeze(new Set()) as Set<string>;
const EMPTY_NUMBER_MAP = Object.freeze(new Map()) as Map<string, number>;

const EMPTY_SIDEBAR_TREE: SidebarTree = {
  channelMap: EMPTY_CHANNEL_MAP,
  membershipMap: EMPTY_MEMBERSHIP_MAP,
  mutedSet: EMPTY_MUTED_SET,
  unreadMap: EMPTY_NUMBER_MAP,
  mentionMap: EMPTY_NUMBER_MAP,
  lastMessageMap: EMPTY_NUMBER_MAP,
};

// ─── Equality on the bundle (per-slice Object.is) ────────────────────
// Inner selectors preserve identity when content hasn't changed, so this
// short-circuits notifications when every slice is stable.
function sidebarTreeEqual(a: SidebarTree, b: SidebarTree): boolean {
  return (
    Object.is(a.channelMap, b.channelMap) &&
    Object.is(a.membershipMap, b.membershipMap) &&
    Object.is(a.mutedSet, b.mutedSet) &&
    Object.is(a.unreadMap, b.unreadMap) &&
    Object.is(a.mentionMap, b.mentionMap) &&
    Object.is(a.lastMessageMap, b.lastMessageMap)
  );
}

// ─── Selector factory ────────────────────────────────────────────────
// Module-level singleton — one closure, one set of `prev` refs, preserved
// across Zero emissions for the lifetime of the process.
export function createSidebarTreeSelector(): (
  rows: readonly MembershipWithRelated[],
) => SidebarTree {
  const channelMap = createIncrementalMapSelector<
    ChannelRow,
    string,
    ChannelRow
  >(
    (c) => c.id as string,
    (c) => c,
    // Channel row equality: we'd ideally field-compare, but channels have
    // many fields + nested members array. Use Object.is on the raw row —
    // Zero's IVM preserves per-row identity when a row didn't change, so
    // this will hit on stat-only ticks (where channels aren't touched).
    // If Zero returns a fresh ref for unchanged channels, we'd re-emit;
    // acceptable tradeoff.
    (prev, next) => prev === next,
  );

  const membershipMap = createIncrementalMapSelector<
    MembershipRow,
    string,
    MembershipRow
  >(
    (m) => m.channel_id as string,
    (m) => m,
    // Same reasoning as channelMap. Membership row identity from Zero IVM.
    (prev, next) => prev === next,
  );

  const unreadMap = createIncrementalMapSelector<
    ChannelStatsRow,
    string,
    number
  >(
    (s) => s.channel_id as string,
    (s) => (s.unread_count > 0 ? (s.unread_count as number) : 0),
    (prev, next) => prev === next,
  );

  const mentionMap = createIncrementalMapSelector<
    ChannelStatsRow,
    string,
    number
  >(
    (s) => s.channel_id as string,
    (s) => (s.mention_count > 0 ? (s.mention_count as number) : 0),
    (prev, next) => prev === next,
  );

  const lastMessageMap = createIncrementalMapSelector<
    ChannelStatsRow,
    string,
    number
  >(
    (s) => s.channel_id as string,
    (s) => (s.last_message_at ? (s.last_message_at as number) : 0),
    (prev, next) => prev === next,
  );

  // Muted set — identity stable when mute flags haven't changed.
  let prevMutedSet = EMPTY_MUTED_SET;
  let prevMutedKey: string | null = null;
  const mutedSetSelector = (rows: readonly MembershipRow[]): Set<string> => {
    const ids: string[] = [];
    for (const m of rows) {
      if (m.muted) ids.push(m.channel_id as string);
    }
    const key = ids.sort().join(",");
    if (key === prevMutedKey) return prevMutedSet;
    prevMutedSet = new Set(ids);
    prevMutedKey = key;
    return prevMutedSet;
  };

  // Last-bundle output + dev log snapshot (tracks what changed emission-
  // over-emission so the composite's behaviour is observable via
  // RENDER_DEBUG).
  let lastLogged: SidebarTree | null = null;

  return (rows: readonly MembershipWithRelated[]): SidebarTree => {
    // Extract slices.
    const channels: ChannelRow[] = [];
    const stats: ChannelStatsRow[] = [];
    for (const row of rows) {
      if (row.channel) channels.push(row.channel);
      if (row.stats) stats.push(row.stats);
    }

    const next: SidebarTree = {
      channelMap: channelMap(channels),
      membershipMap: membershipMap(rows),
      mutedSet: mutedSetSelector(rows),
      unreadMap: unreadMap(stats),
      mentionMap: mentionMap(stats),
      lastMessageMap: lastMessageMap(stats),
    };

    if (RENDER_DEBUG) {
      const prev = lastLogged;
      const tag = (k: keyof SidebarTree) =>
        prev && Object.is(prev[k], next[k]) ? "stable" : "CHANGED";
      console.debug(
        `[sidebar-tree] emission rows=${rows.length} | channelMap=${tag(
          "channelMap",
        )} | membershipMap=${tag(
          "membershipMap",
        )} | unreadMap=${tag("unreadMap")} | mentionMap=${tag(
          "mentionMap",
        )} | lastMessageMap=${tag("lastMessageMap")} | mutedSet=${tag(
          "mutedSet",
        )}`,
      );
      lastLogged = next;
    }

    return next;
  };
}

const sidebarTreeSelector = createSidebarTreeSelector();

// ─── Hook ────────────────────────────────────────────────────────────
/**
 * Subscribes to the Sidebar composite query once and returns six
 * identity-stable slices (channels/memberships/stats maps, muted set).
 *
 * Replaces Sidebar's fan-out of useMyMemberships + useMutedSet +
 * useUnreadCountMap + useMentionCountMap + useLastMessageAtMap +
 * useQuery(channels.byWorkspaceAndUserWithMembers) +
 * useQuery(channels.dmsByUserWithMembers) +
 * useQuery(channels.dmsByWorkspaceAndUserWithMembers). Sidebar itself
 * performs client-side filters (is_archived, DM hidden, workspace scope)
 * against the returned maps.
 */
export function useSidebarTree() {
  const { user } = useCurrentUser();
  return useZeroSelector(
    "sidebar.tree",
    user ? queries.sidebar.myChannelTree() : null,
    sidebarTreeSelector,
    {
      defaultValue: EMPTY_SIDEBAR_TREE,
      isEqual: sidebarTreeEqual,
    },
  );
}
